const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export const COMPANY = {
  name: 'Soren Transport',
  address: '17302 House & Hahl Road',
  cityStateZip: 'CYPRESS, TX 77433',
  paperworkEmail: 'carrierpaperwork@sorentransport.com',
};

export const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

export const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const fmtClock = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const money = (n) => `USD $${(parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const field = (label, value) => `<div class="f"><b>${esc(label)}:</b> ${esc(value)}</div>`;

const stopBlock = (s, idx, total) => {
  const title = idx === 0 ? 'Shipper Information' : idx === total - 1 ? 'Consignee Information' : `Additional Stop ${idx}`;
  const when = s.appointment ? `${fmtDate(s.appointment)} ${fmtTime(s.appointment)}${s.appointmentEnd ? ` - ${fmtClock(s.appointmentEnd)}` : ''}` : 'TBD';
  const whenLabel = s.type === 'pickup' ? 'Pick Up Time' : 'Delivery Date';
  return `
    <div class="stop">
      <div class="stop-title">${esc(title)}${idx !== 0 && idx !== total - 1 ? ` &nbsp;<span class="muted">Stop Type: ${s.type === 'pickup' ? 'Pickup' : 'Delivery'}</span>` : ''}</div>
      <div class="cols">
        <div>
          ${field('Name', s.name)}
          ${field('Address', s.address)}
          ${field('Reference Number', s.reference)}
          ${field('Mileage to Next Stop', s.miles)}
        </div>
        <div>
          ${field('Contact', s.contact)}
          ${field('Phone', s.phone)}
          ${field(whenLabel, when)}
        </div>
      </div>
      <div class="instr"><b>${s.type === 'pickup' ? 'INSTRUCTIONS' : 'DELIVERY INSTRUCTIONS'}:</b> ${esc(s.instructions)}</div>
    </div>`;
};

export const buildRateConfirmationHtml = (rc) => {
  const total = rc.rateItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const logo = `${window.location.origin}/brand/soren-logo.png`;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rate Confirmation - Load ${esc(rc.loadNumber)}</title>
  <style>
    @page { size: letter; margin: 0.5in; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #111; margin: 0; }
    .hdr { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 6px; }
    .hdr h2 { margin: 0; font-size: 13pt; }
    .hdr .addr { font-size: 9pt; line-height: 1.3; }
    .hdr img { height: 44px; }
    .hdr .right { text-align: right; }
    .hdr .loadno { font-size: 12pt; font-weight: 700; margin-top: 4px; }
    h1 { text-align: center; font-size: 14pt; margin: 10px 0 8px; letter-spacing: 0.5px; }
    .cols { display: flex; gap: 18px; }
    .cols > div { flex: 1; }
    .f { margin: 2px 0; }
    .box { border: 1px solid #999; padding: 6px 8px; margin-bottom: 8px; }
    .stop { border: 1px solid #999; padding: 6px 8px; margin-bottom: 6px; page-break-inside: avoid; }
    .stop-title { font-weight: 700; font-size: 10.5pt; border-bottom: 1px solid #ddd; margin-bottom: 4px; padding-bottom: 2px; }
    .muted { font-weight: 400; color: #555; }
    .instr { margin-top: 4px; white-space: pre-wrap; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
    th, td { border: 1px solid #999; padding: 4px 6px; text-align: left; font-size: 9.5pt; }
    th { background: #f0f0f0; }
    td.num, th.num { text-align: right; }
    .total td { font-weight: 700; }
    .terms { font-size: 8pt; line-height: 1.35; text-align: justify; margin-top: 10px; page-break-inside: avoid; }
    .terms p { margin: 4px 0; }
    .sig { display: flex; gap: 24px; margin-top: 18px; page-break-inside: avoid; }
    .sig > div { flex: 1; border-top: 1px solid #111; padding-top: 3px; font-weight: 700; font-size: 9pt; }
    .foot { text-align: center; font-size: 9pt; margin-top: 14px; }
    .foot b { color: #c00; }
    @media print { .noprint { display: none; } }
  </style></head><body>
  <div class="hdr">
    <div>
      <h2>${esc(COMPANY.name)}</h2>
      <div class="addr">${esc(COMPANY.address)}<br/>${esc(COMPANY.cityStateZip)}</div>
    </div>
    <div class="right">
      <img src="${logo}" alt="SOREN" onerror="this.style.display='none'"/>
      <div class="loadno">Load #: ${esc(rc.loadNumber)}</div>
      <div style="font-size:9pt">Date: ${esc(rc.date)}</div>
    </div>
  </div>
  <h1>TRUCKLOAD RATE CONFIRMATION</h1>

  <div class="box cols">
    <div>
      ${field('Carrier Name', rc.carrierName)}
      ${field('MC#', rc.mcNumber)}
      ${field('Carrier Contact', rc.carrierContact)}
      ${field('Carrier Phone', rc.carrierPhone)}
      ${field('Ready Date', rc.readyDate)}
      ${field('Date Needed', rc.dateNeeded)}
      ${field('Service Level', rc.serviceLevel)}
      ${field('Temperature Range', rc.temperatureRange)}
    </div>
    <div>
      ${field('Customer PO', rc.customerPO)}
      ${field('Shipper Ref', rc.shipperRef)}
      ${field('Trailer Type/Size', rc.trailerType)}
      ${field('Reefer Mode', rc.reeferMode)}
      ${field('Driver', rc.driverName)}
      ${field('Driver Phone', rc.driverPhone)}
      ${field('Truck #', rc.truckNumber)}
      ${field('Trailer #', rc.trailerNumber)}
    </div>
  </div>

  ${rc.stops.map((s, i) => stopBlock(s, i, rc.stops.length)).join('')}

  <table>
    <tr><th>Handling Units</th><th>Package Type</th><th>Commodity</th><th class="num">Total Weight</th></tr>
    <tr><td>${esc(rc.handlingUnits)}</td><td>${esc(rc.packageType)}</td><td>${esc(rc.commodity)}</td><td class="num">${esc(rc.totalWeight)}</td></tr>
  </table>

  <table>
    <tr><th>Rate Breakdown</th><th class="num">Amount</th></tr>
    ${rc.rateItems.map(r => `<tr><td>${esc(r.description)}</td><td class="num">${money(r.amount)}</td></tr>`).join('')}
    <tr class="total"><td>TOTAL:</td><td class="num">${money(total)}</td></tr>
  </table>

  ${rc.notes ? `<div class="box"><b>Notes:</b> <span style="white-space:pre-wrap">${esc(rc.notes)}</span></div>` : ''}

  <div class="terms">
    <p>This confirmation governs the movement of the above-referenced freight as of the specified and hereby amends, is incorporated by reference and becomes a part of the certain transportation contract by and between "Broker" and "Contract carrier". Carrier Agrees to sign the confirmation and return it to the broker and carrier shall be conclusively presumed and compensatory that the freight would not have been tendered to Carrier at higher rates and that not shipments handled under such rates will subsequently be subject to a later claim for undercharges. IF AGREED SERVICES ARE FULFILLED, RATES ARE NOT NEGOTIABLE. Carrier is responsible for all delivery appointments. Failure to comply with appointments will result in a penalty of $100.00 Per Appointment.</p>
    <p>The undersigned accepts the referenced shipment on behalf of the carrier and acknowledge as correct the information contained herein, the carrier agrees to the terms of the Master agreement previously executed between our companies. Invoicing by the carrier and payment by ${esc(COMPANY.name)}, constitutes acceptance of this agreement and creates a valid contract for carriage shipment.</p>
    <p>When loading, the driver must count and inspect his/her load. The Driver / Carrier is responsible for piece count and condition of load at time of delivery. For payment of freight charges, we must receive original and signed Bill of landing and Proof of Delivery with a carrier invoice. Payment will be made 30 days after all required paperwork is received at ${esc(COMPANY.name)}, facilities. We are not responsible for Overweight. If Dimensions, Weight, Quantity or type of commodity are different than those consigned in our Load Confirmation Agreement, the carrier or Broker contracted MUST notify ${esc(COMPANY.name)} Before picking up and request a WRITTEN AUTHORIZATION. ${esc(COMPANY.name)} will not pay any extra charges without AUTHORIZATION.</p>
  </div>

  <div class="foot">Please send your invoice and proof of delivery to <b>${esc(COMPANY.paperworkEmail)}</b></div>

  <div class="sig">
    <div>Carrier Signature:</div>
    <div>MC#:</div>
    <div>Driver Name:</div>
    <div>Driver Phone#:</div>
  </div>

  <div class="foot" style="margin-top:22px">Please call immediately with any questions, concerns, or problems!<br/>
  Send Invoicing to: ${esc(COMPANY.name)} | ${esc(COMPANY.address)} | ${esc(COMPANY.cityStateZip)}</div>
  </body></html>`;
};
